import { Controller } from "stimulus";

// Ticks an elapsed duration ("3:07", "1:12:04") from a `data-since` ISO8601 timestamp, once a
// second. Progressive enhancement: the server renders the opening value so the label is right
// without JS, and this only keeps it moving.
//
// With `staleAfterValue` set it also swaps presentation once `since` is older than that many
// seconds - every `fresh` target is hidden and every `stale` target revealed. Both variants are
// server-rendered so no Tailwind class is ever assembled in JS. The stream health panel uses
// this to downgrade its pill to "Unknown" when the reconcile job stops refreshing the reading.
export default class extends Controller {
  static targets = ["output", "fresh", "stale"];

  static values = {
    since: String,
    // "clock" ticks a duration ("3:07"); "prose" reads as a sentence fragment ("42 min"), for
    // copy like "started 42 min ago" where a colon-separated clock reads as the wrong thing.
    format: { type: String, default: "clock" },
    staleAfter: { type: Number, default: 0 },
    interval: { type: Number, default: 1000 },
  };

  connect() {
    this.since = this.parseSince();
    if (this.since === null) return; // missing or unparseable - leave the server-rendered value alone

    this.tick();
    this.timer = setInterval(() => this.tick(), this.intervalValue);
  }

  disconnect() {
    this.stop();
  }

  // Fires once before connect() on first render (timer is unset, so this no-ops); afterwards it
  // covers a Turbo update that rewrites the attribute in place rather than replacing the element.
  sinceValueChanged() {
    if (!this.timer) return;

    this.stop();
    this.connect();
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  tick() {
    const seconds = Math.max(0, Math.floor((Date.now() - this.since) / 1000));

    this.outputTargets.forEach((el) => {
      el.textContent = this.format(seconds);
    });

    if (this.staleAfterValue > 0) {
      const stale = seconds >= this.staleAfterValue;
      this.freshTargets.forEach((el) => {
        el.hidden = stale;
      });
      this.staleTargets.forEach((el) => {
        el.hidden = !stale;
      });
    }
  }

  parseSince() {
    const parsed = Date.parse(this.sinceValue);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Mirrors DatetimeHelper#elapsed_clock and #elapsed_prose so the first paint and the first tick
  // agree - any drift here shows up as a visible jump a second after the page loads.
  format(seconds) {
    if (this.formatValue === "prose") return this.prose(seconds);

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainder = String(seconds % 60).padStart(2, "0");

    if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${remainder}`;
    return `${minutes}:${remainder}`;
  }

  prose(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
    if (minutes > 0) return `${minutes} min`;
    return "less than a minute";
  }
}
